import type { DecisionKind, Size, Tier } from "./types.ts";

/** The four things a human does on the Bridge (docs/design/bridge.md). */
export const VERBS = ["approve", "advise", "steer", "act"] as const;
export type Verb = (typeof VERBS)[number];

/** Steer has no decision kind of its own: it is always derived from run/task state. */
export function verbForDecisionKind(kind: DecisionKind): Verb {
  switch (kind) {
    case "review_change":
    case "spot_check":
    case "approve_spec":
      return "approve";
    case "question":
    case "exception":
    case "risk_acceptance":
      return "advise";
    case "human_action":
      return "act";
    default: {
      const never: never = kind;
      throw new Error(`unhandled decision kind ${String(never)}`);
    }
  }
}

export const STEER_REASONS = ["idle", "failed_verifications", "budget_hit", "overlap", "merge_failed", "abandoned_dependency", "abandoned_merged", "message_loop", "needs_login", "no_builder"] as const;
export type SteerReason = (typeof STEER_REASONS)[number];

export type SteerItem = {
  reason: SteerReason;
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  runId: string | null;
  message: string;
  ageMin: number;
  /**
   * Set only on `reason: "needs_login"`: who must run the connect command (the agent's owner —
   * "they hold the seat, nobody else can fix it", task #111) and the exact command, straight from
   * `CloudHost.connectCommand()`. Never a Claude credential or session token — Roomate is
   * contractually out of that path (Anthropic's terms); these fields carry only a command string.
   */
  agentId?: string;
  agentName?: string;
  ownerId?: string;
  ownerName?: string;
  connectCommand?: string;
};

export type IdleRunInput = { runId: string; taskId: string; taskNumber: number; taskTitle: string; lastActivityAt: number };
export type FailedVerificationInput = { taskId: string; taskNumber: number; taskTitle: string; failCount: number; lastFailedAt: number };
export type BudgetHitInput = { runId: string; taskId: string; taskNumber: number; taskTitle: string; finishedAt: number };
export type OverlapPairInput = { taskId: string; taskNumber: number; taskTitle: string; otherTaskId: string; otherTaskNumber: number; otherTaskTitle: string };
export type MergeFailedInput = {
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  output: string | null;
  failedAt: number;
  /** Set once a resolve-merge task has been created for this failure, so the item reads as owned, not dropped. */
  resolveTaskNumber: number | null;
};

/** One abandoned task whose direct, still-open dependents need the abandoning person's decision. */
export type AbandonedDependencyInput = {
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  abandonedAt: number;
  dependents: { number: number; title: string }[];
};

/** A node finished an already in-flight merge after a person abandoned the task. */
export type AbandonedMergedInput = {
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  mergedSha: string | null;
  mergedAt: number;
};

/** A ready task whose areas fit no active agent (`@roomate/core`'s `areaFitReport`). */
export type NoBuilderInput = {
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  areas: string[];
  suggestedSplit: { name: string; areas: string[] }[];
  uncovered: string[];
  /** True when a person is the builder: then this is somebody's work, not a stall. */
  humanBuilder: boolean;
  readyAt: number;
  /** Set when the task is parked as too big; the message then names the split and ignores areas. */
  oversized?: { tier: Tier; size: Size; points: number; splitTaskNumber: number | null; /** A valid split is waiting for a person's approval. */ proposed?: boolean };
};

/**
 * One row per message thread (a thread is always two actors; no group threads in the messaging
 * spec, so "two actors exchange" is just the thread's own traffic). `taskId` is null when the
 * thread has no task, in which case it never raises a Steer item — a SteerItem is anchored to a
 * task (spec §10, a known gap); `taskNumber`/`taskTitle` are null together with it.
 */
export type MessageLoopInput = {
  threadId: string;
  taskId: string | null;
  taskNumber: number | null;
  taskTitle: string | null;
  /** Total messages in the thread. */
  messageCount: number;
  /** Most messages exchanged between the two actors within any 10-minute window of the thread. */
  maxExchangeCount: number;
  lastMessageAt: number;
};

/** Why the agent's owner needs to reconnect: a bad login state on a heartbeat, or the provisioner finding the sandbox's snapshot (and the login on it) was lost and recreated. */
export const NEEDS_LOGIN_CAUSES = ["missing", "expired", "created_fresh"] as const;
export type NeedsLoginCause = (typeof NEEDS_LOGIN_CAUSES)[number];

/**
 * One cloud agent currently blocked on a human login, always tied to the task/run it was trying
 * to work on (dec_5az7gf193r: one sandbox per agent, so this is always the owner's problem, never
 * a teammate's). `connectCommand` is the exact string from `CloudHost.connectCommand()` — this
 * type never carries anything else from that terminal.
 */
export type NeedsLoginInput = {
  agentId: string;
  agentName: string;
  ownerId: string;
  ownerName: string;
  connectCommand: string;
  cause: NeedsLoginCause;
  reportedAt: number;
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  runId: string | null;
};

/**
 * Steer items are redirects for work already in flight; nobody is "assigned" one, so unlike
 * decisions they are recomputed on every read rather than stored.
 */
export function deriveSteerItems(input: {
  now: number;
  idleMinutes?: number;
  failThreshold?: number;
  messageLoopThreshold?: number;
  messageExchangeThreshold?: number;
  idleRuns: readonly IdleRunInput[];
  failedVerifications: readonly FailedVerificationInput[];
  budgetHitRuns: readonly BudgetHitInput[];
  overlapPairs: readonly OverlapPairInput[];
  mergeFailedTasks?: readonly MergeFailedInput[];
  abandonedDependencies?: readonly AbandonedDependencyInput[];
  abandonedMergedTasks?: readonly AbandonedMergedInput[];
  messageLoops?: readonly MessageLoopInput[];
  needsLogin?: readonly NeedsLoginInput[];
  noBuilderTasks?: readonly NoBuilderInput[];
}): SteerItem[] {
  const idleThresholdMs = (input.idleMinutes ?? 10) * 60_000;
  const failThreshold = input.failThreshold ?? 3;
  const messageLoopThreshold = input.messageLoopThreshold ?? 12;
  const messageExchangeThreshold = input.messageExchangeThreshold ?? 6;
  const items: SteerItem[] = [];

  for (const r of input.idleRuns) {
    const ageMin = Math.round((input.now - r.lastActivityAt) / 60_000);
    if (input.now - r.lastActivityAt < idleThresholdMs) continue;
    items.push({
      reason: "idle",
      taskId: r.taskId,
      taskNumber: r.taskNumber,
      taskTitle: r.taskTitle,
      runId: r.runId,
      message: `no activity for ${ageMin} min`,
      ageMin,
    });
  }

  for (const t of input.failedVerifications) {
    if (t.failCount < failThreshold) continue;
    items.push({
      reason: "failed_verifications",
      taskId: t.taskId,
      taskNumber: t.taskNumber,
      taskTitle: t.taskTitle,
      runId: null,
      message: `${t.failCount} failed verifications`,
      ageMin: Math.round((input.now - t.lastFailedAt) / 60_000),
    });
  }

  for (const r of input.budgetHitRuns) {
    items.push({
      reason: "budget_hit",
      taskId: r.taskId,
      taskNumber: r.taskNumber,
      taskTitle: r.taskTitle,
      runId: r.runId,
      message: "hit its budget",
      ageMin: Math.round((input.now - r.finishedAt) / 60_000),
    });
  }

  const seenPairs = new Set<string>();
  for (const o of input.overlapPairs) {
    const key = [o.taskId, o.otherTaskId].sort().join("|");
    if (seenPairs.has(key)) continue;
    seenPairs.add(key);
    items.push({
      reason: "overlap",
      taskId: o.taskId,
      taskNumber: o.taskNumber,
      taskTitle: o.taskTitle,
      runId: null,
      message: `overlaps with #${o.otherTaskNumber} ${o.otherTaskTitle}`,
      ageMin: 0,
    });
  }

  for (const t of input.mergeFailedTasks ?? []) {
    const base = `merge failed${t.output ? `: ${t.output.split("\n")[0]!.slice(0, 200)}` : ""}`;
    items.push({
      reason: "merge_failed",
      taskId: t.taskId,
      taskNumber: t.taskNumber,
      taskTitle: t.taskTitle,
      runId: null,
      message: t.resolveTaskNumber ? `${base}; an agent is resolving it on #${t.resolveTaskNumber}` : base,
      ageMin: Math.round((input.now - t.failedAt) / 60_000),
    });
  }

  for (const t of input.abandonedDependencies ?? []) {
    if (t.dependents.length === 0) continue;
    const dependents = t.dependents.map((d) => `#${d.number} ${d.title}`).join(", ");
    items.push({
      reason: "abandoned_dependency",
      taskId: t.taskId,
      taskNumber: t.taskNumber,
      taskTitle: t.taskTitle,
      runId: null,
      message: `${dependents} ${t.dependents.length === 1 ? "is" : "are"} blocked by abandoned #${t.taskNumber}`,
      ageMin: Math.round((input.now - t.abandonedAt) / 60_000),
    });
  }

  for (const t of input.abandonedMergedTasks ?? []) {
    items.push({
      reason: "abandoned_merged",
      taskId: t.taskId,
      taskNumber: t.taskNumber,
      taskTitle: t.taskTitle,
      runId: null,
      message: `#${t.taskNumber} was abandoned but its branch is on main${t.mergedSha ? ` at ${t.mergedSha}` : ""}; revert or confirm`,
      ageMin: Math.round((input.now - t.mergedAt) / 60_000),
    });
  }

  for (const t of input.messageLoops ?? []) {
    // The three are null together by contract; requiring all three keeps a partially-missing
    // join from surfacing a thread as if it had a task, rather than silently dropping it.
    if (t.taskId === null || t.taskNumber === null || t.taskTitle === null) continue;
    const overThread = t.messageCount > messageLoopThreshold;
    const overExchange = t.maxExchangeCount > messageExchangeThreshold;
    if (!overThread && !overExchange) continue;
    items.push({
      reason: "message_loop",
      taskId: t.taskId,
      taskNumber: t.taskNumber,
      taskTitle: t.taskTitle,
      runId: null,
      message: overThread ? `${t.messageCount} messages in one thread` : `${t.maxExchangeCount} messages between two actors in 10 min`,
      ageMin: Math.round((input.now - t.lastMessageAt) / 60_000),
    });
  }

  const needsLoginPhrase: Record<NeedsLoginCause, string> = {
    missing: "not logged in",
    expired: "login expired",
    created_fresh: "lost its login (snapshot recreated)",
  };
  // Keyed by agent, not task: dec_5az7gf193r means one sandbox per agent, so two reports for the
  // same agent (e.g. a stale "expired" heartbeat still queued behind a fresher one, or the
  // provisioner's createdFresh landing alongside a heartbeat) are the same problem, not two — kept
  // by the latest `reportedAt`, independent of input order. On an exact `reportedAt` tie (both
  // stamped from the same clock tick), `created_fresh` wins over `expired`/`missing`: it's the
  // provisioner's own signal that the snapshot — and the login with it — is gone, which is more
  // authoritative than a heartbeat's stale-login guess for the same instant.
  const causePriority: Record<NeedsLoginCause, number> = { missing: 0, expired: 1, created_fresh: 2 };
  const latestNeedsLoginByAgent = new Map<string, NeedsLoginInput>();
  for (const n of input.needsLogin ?? []) {
    const current = latestNeedsLoginByAgent.get(n.agentId);
    if (
      !current ||
      n.reportedAt > current.reportedAt ||
      (n.reportedAt === current.reportedAt && causePriority[n.cause] >= causePriority[current.cause])
    ) {
      latestNeedsLoginByAgent.set(n.agentId, n);
    }
  }
  for (const n of latestNeedsLoginByAgent.values()) {
    items.push({
      reason: "needs_login",
      taskId: n.taskId,
      taskNumber: n.taskNumber,
      taskTitle: n.taskTitle,
      runId: n.runId,
      message: `${n.agentName}: ${needsLoginPhrase[n.cause]} — run: ${n.connectCommand}`,
      ageMin: Math.round((input.now - n.reportedAt) / 60_000),
      agentId: n.agentId,
      agentName: n.agentName,
      ownerId: n.ownerId,
      ownerName: n.ownerName,
      connectCommand: n.connectCommand,
    });
  }

  for (const t of input.noBuilderTasks ?? []) {
    if (t.humanBuilder) continue;
    const message = t.oversized
      ? `too big for one agent run (${t.oversized.tier}, ${t.oversized.points} points); ${
          t.oversized.proposed
            ? "split proposal awaiting approval"
            : t.oversized.splitTaskNumber === null
              ? "no spec agent to split it"
              : `split task #${t.oversized.splitTaskNumber}`
        }`
      : t.uncovered.length > 0
        ? `no agent covers ${t.uncovered.join(", ")}`
        : t.suggestedSplit.length > 0
          ? `areas span ${t.suggestedSplit.length} agents; split: ${t.suggestedSplit.map((s) => `${s.areas.join(", ")} → ${s.name}`).join(", ")}`
          : `no agent may build areas: ${t.areas.join(", ")}`;
    items.push({
      reason: "no_builder",
      taskId: t.taskId,
      taskNumber: t.taskNumber,
      taskTitle: t.taskTitle,
      runId: null,
      message,
      ageMin: Math.round((input.now - t.readyAt) / 60_000),
    });
  }

  return items;
}
